import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = "block text-lg font-medium text-stone-900 mb-2";
const FIELD =
  "h-12 w-full rounded-xl border-2 border-stone-300 bg-white px-4 text-lg text-stone-900 focus-visible:border-orange-600 placeholder:text-stone-500";

const CONTACT_METHODS = ["Email", "Phone", "Either"];
const RELATIONSHIPS = [
  "I am enquiring for myself",
  "Parent",
  "Family member",
  "Carer",
  "Social worker or commissioner",
  "Referral partner",
  "Other",
];
const ENQUIRY_TYPES = [
  "General enquiry",
  "Referral",
  "Arrange a visit or tour",
  "Careers",
  "Other",
];

const EMPTY = {
  name: "",
  preferred_contact: "",
  contact_detail: "",
  relationship: "",
  enquiry_type: "",
  message: "",
  consent: false,
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.preferred_contact) er.preferred_contact = "Please choose a contact method.";
    if (!form.contact_detail.trim()) er.contact_detail = "Please enter an email or phone number.";
    if (!form.relationship) er.relationship = "Please choose an option.";
    if (!form.enquiry_type) er.enquiry_type = "Please choose an enquiry type.";
    if (!form.message.trim()) er.message = "Please tell us how we can help.";
    if (!form.consent) er.consent = "Please confirm you have read the Privacy Policy.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the form and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enquiry`, form);
      setSent(true);
      toast.success(res.data?.message || "Your enquiry has been sent.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please use the contact details on this page.");
    } finally {
      setSubmitting(false);
    }
  };

  const ErrorText = ({ id, children }) =>
    children ? (
      <p id={id} className="mt-2 text-base text-red-700 font-medium" role="alert">
        {children}
      </p>
    ) : null;

  if (sent) {
    return (
      <div
        className="rounded-2xl border-2 border-green-200 bg-green-50 p-8 text-center"
        data-testid="enquiry-success"
        role="status"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-700" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-2xl font-semibold text-stone-900">Thank you</h3>
        <p className="mt-2 text-lg text-stone-700">
          Your enquiry has been sent. We will get back to you as soon as we can.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-6 px-6 py-3 rounded-xl bg-white border-2 border-stone-300 font-semibold text-lg hover:border-orange-600 hover:bg-orange-50"
          data-testid="enquiry-send-another-button"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="enquiry-form">
      <div
        className="flex items-start gap-3 rounded-2xl border-2 border-orange-200 bg-orange-50 p-5"
        data-testid="enquiry-notice"
      >
        <AlertTriangle className="h-6 w-6 text-orange-700 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-base text-stone-800 leading-relaxed">
          Please do not include medical information, diagnoses, care plans or urgent safeguarding
          concerns in this form. For urgent matters, contact us directly using the details above.
        </p>
      </div>

      <div>
        <label htmlFor="name" className={LABEL}>
          Your name
        </label>
        <input
          id="name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={set("name")}
          aria-required="true"
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
          className={FIELD}
          data-testid="enquiry-name-input"
        />
        <ErrorText id="name-error">{errors.name}</ErrorText>
      </div>

      <div>
        <label htmlFor="preferred_contact" className={LABEL}>
          Preferred contact method
        </label>
        <select
          id="preferred_contact"
          value={form.preferred_contact}
          onChange={set("preferred_contact")}
          aria-required="true"
          aria-invalid={!!errors.preferred_contact}
          aria-describedby={errors.preferred_contact ? "preferred_contact-error" : undefined}
          className={FIELD}
          data-testid="enquiry-contact-method-select"
        >
          <option value="">Please choose…</option>
          {CONTACT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <ErrorText id="preferred_contact-error">{errors.preferred_contact}</ErrorText>
      </div>

      <div>
        <label htmlFor="contact_detail" className={LABEL}>
          Email address or phone number
        </label>
        <input
          id="contact_detail"
          type="text"
          value={form.contact_detail}
          onChange={set("contact_detail")}
          aria-required="true"
          aria-invalid={!!errors.contact_detail}
          aria-describedby={errors.contact_detail ? "contact_detail-error" : undefined}
          className={FIELD}
          data-testid="enquiry-contact-detail-input"
        />
        <ErrorText id="contact_detail-error">{errors.contact_detail}</ErrorText>
      </div>

      <div>
        <label htmlFor="relationship" className={LABEL}>
          Your relationship to the person making an enquiry
        </label>
        <select
          id="relationship"
          value={form.relationship}
          onChange={set("relationship")}
          aria-required="true"
          aria-invalid={!!errors.relationship}
          aria-describedby={errors.relationship ? "relationship-error" : undefined}
          className={FIELD}
          data-testid="enquiry-relationship-select"
        >
          <option value="">Please choose…</option>
          {RELATIONSHIPS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <ErrorText id="relationship-error">{errors.relationship}</ErrorText>
      </div>

      <div>
        <label htmlFor="enquiry_type" className={LABEL}>
          Enquiry type
        </label>
        <select
          id="enquiry_type"
          value={form.enquiry_type}
          onChange={set("enquiry_type")}
          aria-required="true"
          aria-invalid={!!errors.enquiry_type}
          aria-describedby={errors.enquiry_type ? "enquiry_type-error" : undefined}
          className={FIELD}
          data-testid="enquiry-type-select"
        >
          <option value="">Please choose…</option>
          {ENQUIRY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <ErrorText id="enquiry_type-error">{errors.enquiry_type}</ErrorText>
      </div>

      <div>
        <label htmlFor="message" className={LABEL}>
          Your message
        </label>
        <textarea
          id="message"
          rows={5}
          value={form.message}
          onChange={set("message")}
          aria-required="true"
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "message-error" : undefined}
          className="w-full rounded-xl border-2 border-stone-300 bg-white px-4 py-3 text-lg text-stone-900 focus-visible:border-orange-600 placeholder:text-stone-500"
          data-testid="enquiry-message-input"
        />
        <ErrorText id="message-error">{errors.message}</ErrorText>
      </div>

      <div>
        <div className="flex items-start gap-3">
          <input
            id="consent"
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            aria-required="true"
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "consent-error" : undefined}
            className="mt-1.5 h-6 w-6 shrink-0 rounded border-2 border-stone-400 text-orange-600 accent-orange-600"
            data-testid="enquiry-consent-checkbox"
          />
          <label htmlFor="consent" className="text-lg text-stone-800 leading-relaxed">
            I have read the{" "}
            <Link
              to="/privacy-policy"
              className="font-semibold text-orange-700 underline"
              data-testid="enquiry-privacy-link"
            >
              Privacy Policy
            </Link>{" "}
            and understand how Spire Inclusion will use my enquiry details.
          </label>
        </div>
        <ErrorText id="consent-error">{errors.consent}</ErrorText>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-orange-600 text-white font-semibold text-lg hover:bg-orange-700 transition-colors disabled:opacity-70"
        data-testid="enquiry-submit-button"
      >
        {submitting && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        {submitting ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
};
